import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'lib-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ModalComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() title: string;
  @Input() subTitle: string;
  @Input() showCloseButton: boolean;
  @Input() showPrimaryButton: boolean;
  @Input() primaryButtonText: string;
  @Input() disablePrimaryButton: boolean;
  @Input() disableSecondaryButton: boolean;
  @Input() disableCloseButton: boolean;
  @Input() showSecondaryButton: boolean;
  @Input() secondaryButtonText: string;
  @Input() showLightButton: boolean;
  @Input() lightButtonText: string;
  @Input() disableLightButton: boolean;
  @Input() showAltButton: boolean;
  @Input() altButtonText: string;
  @Input() showRemoveButton: boolean;
  @Input() removeButtonText: string;
  @Input() isLoading: boolean;

  @Output() closeModal = new EventEmitter<void>();
  @Output() primaryButton = new EventEmitter<void>();
  @Output() secondaryButton = new EventEmitter<void>();
  @Output() altButton = new EventEmitter<void>();
  @Output() removeButton = new EventEmitter<void>();
  @Output() lightButton = new EventEmitter<void>();

  closeLabel: string;
  firstFocus = true;
  @ViewChild('modalRef') modalRef: ElementRef;

  // Unikt id per modalinstans så att aria-labelledby inte pekar på dubbletter
  // när flera modaler är öppna samtidigt.
  private static nextTitleId = 0;
  titleId = `modal-title-${ModalComponent.nextTitleId++}`;
  private viewInitialized = false;

  constructor(private translocoService: TranslocoService) {}

  ngOnInit(): void {
    this.closeLabel =
      this.translocoService.getActiveLang() === 'sv' ? 'Stäng' : 'Close';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['title'] && this.viewInitialized) {
      this.updateAriaLabelledBy();
    }
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.updateAriaLabelledBy();
  }

  private updateAriaLabelledBy(): void {
    const modalContainer = this.modalRef?.nativeElement?.closest('.modal');
    if (!modalContainer) {
      return;
    }

    if (this.title) {
      modalContainer.setAttribute('aria-labelledby', this.titleId);
    } else {
      modalContainer.removeAttribute('aria-labelledby');
    }
  }

  startFocus() {
    if (this.firstFocus) {
      this.firstFocus = false;
      this.endFocus();
      return;
    }

    const focusable = this.modalRef.nativeElement.querySelectorAll(
      'input, textarea, button, a, [tabindex="0"]:not(.focus-anchor)',
    );
    if (focusable) {
      const filtered = [...focusable].filter(
        (a) => a.offsetWidth > 0 || a.offsetHeight > 0,
      );
      if (filtered.length > 0) {
        filtered.at(-1).focus();
      }
    }
  }

  endFocus() {
    if (this.firstFocus) {
      this.firstFocus = false;
      this.startFocus();
      return;
    }

    const focusable = this.modalRef.nativeElement.querySelectorAll(
      'input, textarea, button, a, [tabindex="0"]:not(.focus-anchor)',
    );
    const firstVisible = [...focusable].find(
      (a) => a.offsetWidth > 0 || a.offsetHeight > 0,
    );
    firstVisible.focus();
  }
}
